/**
 * useChat — React hook for WebSocket-based real-time messaging.
 *
 * @param {string} [currentUserId] — viewer user id (for unread + read receipts)
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { API_BASE, getToken } from '../api/core'
import { chatApi } from '../api/chat'

const WS_BASE = API_BASE
    .replace('https://', 'wss://')
    .replace('http://', 'ws://')
const HISTORY_PAGE_SIZE = 50

/** Reconnect backoff: 1s, 2s, 4s, 8s, max 30s */
function backoff(attempt) {
    return Math.min(1000 * Math.pow(2, attempt), 30000)
}

function deliveryStatusFromCounts(nOthers, nRead) {
    if (nOthers <= 0) return 'sent'
    if (nRead >= nOthers) return 'read'
    if (nRead > 0) return 'delivered'
    return 'sent'
}

function mergeMessagesById(prev, incoming, { prepend = false } = {}) {
    if (!Array.isArray(incoming) || incoming.length === 0) return prev
    const byId = new Map()
    const ordered = prepend ? [...incoming, ...prev] : [...prev, ...incoming]
    for (const msg of ordered) {
        if (!msg?.id) continue
        byId.set(msg.id, { ...(byId.get(msg.id) || {}), ...msg })
    }
    return Array.from(byId.values()).sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
}

export function useChat(currentUserId = undefined) {
    const [rooms, setRooms] = useState([])
    const [activeRoom, setActiveRoom] = useState(null)
    const [messages, setMessages] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isConnected, setIsConnected] = useState(false)
    const [typingUsers, setTypingUsers] = useState({})
    const [totalUnread, setTotalUnread] = useState(0)
    const [hasMoreHistory, setHasMoreHistory] = useState(false)
    const [isLoadingMore, setIsLoadingMore] = useState(false)

    const wsRef = useRef(null)
    const reconnectAttempt = useRef(0)
    const reconnectTimer = useRef(null)
    const typingTimers = useRef({})
    const activeRoomIdRef = useRef(null)
    const currentUserIdRef = useRef(currentUserId)
    const participantsCountRef = useRef(2)
    const historyCursorRef = useRef(null)
    const messagesRef = useRef([])

    useEffect(() => {
        messagesRef.current = messages
    }, [messages])

    useEffect(() => {
        currentUserIdRef.current = currentUserId
    }, [currentUserId])

    useEffect(() => {
        activeRoomIdRef.current = activeRoom?.id ?? null
        const n = activeRoom?.participant_count ?? activeRoom?.participants?.length ?? 2
        participantsCountRef.current = Math.max(1, n)
    }, [activeRoom])

    const handleWsMessage = useCallback((data) => {
        switch (data.type) {
            case 'new_message': {
                const msg = data.message
                setMessages(prev => {
                    if (activeRoomIdRef.current === data.room_id) {
                        if (prev.some(m => m.id === msg.id)) return prev
                        return [...prev, msg]
                    }
                    return prev
                })
                setRooms(prev => prev.map(r => {
                    if (r.id !== data.room_id) return r
                    const isActive = activeRoomIdRef.current === data.room_id
                    return {
                        ...r,
                        last_message: {
                            content: msg.content?.slice(0, 100),
                            sender_name: msg.sender_name || msg.sender?.full_name || null,
                            created_at: msg.created_at,
                            message_type: msg.message_type,
                        },
                        updated_at: msg.created_at,
                        unread_count: isActive ? (r.unread_count || 0) : (r.unread_count || 0) + 1,
                    }
                }).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)))
                break
            }

            case 'typing': {
                const key = `${data.room_id}-${data.user_id}`
                if (data.is_typing) {
                    setTypingUsers(prev => ({
                        ...prev,
                        [data.room_id]: [
                            ...(prev[data.room_id] || []).filter(u => u.userId !== data.user_id),
                            { userId: data.user_id, name: data.user_name },
                        ],
                    }))
                    clearTimeout(typingTimers.current[key])
                    typingTimers.current[key] = setTimeout(() => {
                        setTypingUsers(prev => ({
                            ...prev,
                            [data.room_id]: (prev[data.room_id] || []).filter(u => u.userId !== data.user_id),
                        }))
                    }, 3000)
                } else {
                    setTypingUsers(prev => ({
                        ...prev,
                        [data.room_id]: (prev[data.room_id] || []).filter(u => u.userId !== data.user_id),
                    }))
                }
                break
            }

            case 'read_update': {
                setRooms(prev => prev.map(r =>
                    r.id === data.room_id
                        ? {
                            ...r,
                            current_user_last_read_at:
                                String(r.id) === String(data.room_id) && String(data.user_id) === String(currentUserIdRef.current)
                                    ? data.timestamp
                                    : r.current_user_last_read_at,
                        }
                        : r
                ))
                setActiveRoom(prev =>
                    prev && prev.id === data.room_id && String(data.user_id) === String(currentUserIdRef.current)
                        ? { ...prev, current_user_last_read_at: data.timestamp }
                        : prev
                )
                break
            }

            case 'message_read': {
                const mid = data.message_id
                const readerId = data.user_id
                if (!mid || !readerId) break
                const me = currentUserIdRef.current
                const nOthers = Math.max(0, participantsCountRef.current - 1)
                setMessages(prev => prev.map(m => {
                    if (m.id !== mid || m.sender_id !== me) return m
                    const receipts = [...(m.read_receipts || [])]
                    if (!receipts.some(x => String(x.user_id) === String(readerId))) {
                        receipts.push({
                            user_id: readerId,
                            read_at: data.read_at,
                            full_name: null,
                        })
                    }
                    return {
                        ...m,
                        read_receipts: receipts,
                        delivery_status: deliveryStatusFromCounts(nOthers, receipts.length),
                    }
                }))
                break
            }

            case 'reaction_update': {
                setMessages(prev => prev.map(m =>
                    m.id === data.message_id ? { ...m, reactions: data.reactions } : m
                ))
                break
            }

            case 'message_edited': {
                const edited = data.message
                if (!edited?.id) break
                setMessages(prev => prev.map(m => (m.id === edited.id ? { ...m, ...edited } : m)))
                setRooms(prev => prev.map(r => {
                    if (r.id !== data.room_id) return r
                    const isLast = r.last_message && (r.last_message.created_at === edited.created_at)
                    return isLast
                        ? {
                            ...r,
                            last_message: {
                                ...r.last_message,
                                content: edited.content?.slice(0, 100),
                            },
                        }
                        : r
                }))
                break
            }

            case 'message_deleted': {
                if (!data?.message_id) break
                setMessages(prev => prev.map(m =>
                    m.id === data.message_id
                        ? { ...m, is_deleted: true, content: 'Сообщение удалено' }
                        : m
                ))
                break
            }

            case 'room_pinned_update': {
                const rid = data.room_id
                if (!rid) break
                setRooms(prev => prev.map(r =>
                    r.id === rid
                        ? {
                            ...r,
                            pinned_message_id: data.pinned_message_id ?? null,
                            pinned_message: data.pinned_message ?? null,
                        }
                        : r,
                ))
                setActiveRoom(prev =>
                    prev && prev.id === rid
                        ? {
                            ...prev,
                            pinned_message_id: data.pinned_message_id ?? null,
                            pinned_message: data.pinned_message ?? null,
                        }
                        : prev,
                )
                break
            }

            case 'feedback': {
                break
            }

            default:
                break
        }
    }, [])

    const connect = useCallback(() => {
        const token = getToken()
        if (!token) return

        const wsUrl = `${WS_BASE}/chat/ws?token=${encodeURIComponent(token)}`
        const ws = new WebSocket(wsUrl)

        ws.onopen = () => {
            setIsConnected(true)
            reconnectAttempt.current = 0
            console.info('[Chat WS] Connected')
        }

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data)
                handleWsMessage(data)
            } catch (e) {
                console.warn('[Chat WS] Parse error:', e)
            }
        }

        ws.onclose = (event) => {
            setIsConnected(false)
            console.info(`[Chat WS] Disconnected (code=${event.code})`)

            if (event.code !== 4001 && event.code !== 1000) {
                const delay = backoff(reconnectAttempt.current)
                reconnectAttempt.current += 1
                reconnectTimer.current = setTimeout(connect, delay)
            }
        }

        ws.onerror = () => {
            ws.close()
        }

        wsRef.current = ws
    }, [handleWsMessage])

    const disconnect = useCallback(() => {
        clearTimeout(reconnectTimer.current)
        if (wsRef.current) {
            wsRef.current.close(1000, 'User disconnect')
            wsRef.current = null
        }
    }, [])

    const wsSend = useCallback((payload) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(payload))
        }
    }, [])

    const sendTyping = useCallback((roomId, isTyping = true) => {
        wsSend({ type: 'typing', room_id: roomId, is_typing: isTyping })
    }, [wsSend])

    const loadRooms = useCallback(async () => {
        try {
            const data = await chatApi.listRooms()
            setRooms(data || [])
            const unread = (data || []).reduce((sum, r) => sum + (r.unread_count || 0), 0)
            setTotalUnread(unread)
        } catch (e) {
            console.error('[Chat] Failed to load rooms:', e)
        }
    }, [])

    const loadMessages = useCallback(async (roomId) => {
        setIsLoading(true)
        try {
            const data = await chatApi.listMessages(roomId, { limit: HISTORY_PAGE_SIZE })
            const items = data || []
            setMessages(items)
            historyCursorRef.current = items.length > 0 ? items[0].created_at : null
            setHasMoreHistory(items.length === HISTORY_PAGE_SIZE)
            const uid = currentUserIdRef.current
            if (uid && data?.length) {
                await Promise.all(
                    data
                        .filter(m => m.sender_id && m.sender_id !== uid)
                        .map(m => chatApi.markMessageRead(m.id).catch(() => {}))
                )
            }
        } catch (e) {
            console.error('[Chat] Failed to load messages:', e)
        } finally {
            setIsLoading(false)
        }
    }, [])

    const loadMoreMessages = useCallback(async () => {
        if (!activeRoom?.id || !historyCursorRef.current || isLoadingMore || !hasMoreHistory) return []
        setIsLoadingMore(true)
        try {
            const older = await chatApi.listMessages(activeRoom.id, {
                limit: HISTORY_PAGE_SIZE,
                before: historyCursorRef.current,
            })
            const items = older || []
            if (items.length > 0) {
                historyCursorRef.current = items[0].created_at
                setMessages(prev => mergeMessagesById(prev, items, { prepend: true }))
            }
            setHasMoreHistory(items.length === HISTORY_PAGE_SIZE)
            return items
        } catch (e) {
            console.error('[Chat] Failed to load more messages:', e)
            return []
        } finally {
            setIsLoadingMore(false)
        }
    }, [activeRoom?.id, hasMoreHistory, isLoadingMore])

    const ensureMessageLoaded = useCallback(async (messageId, maxPages = 6) => {
        if (!messageId) return false
        let page = 0
        while (page < maxPages) {
            if (messagesRef.current.some(m => String(m.id) === String(messageId))) return true
            if (!hasMoreHistory) return false
            const batch = await loadMoreMessages()
            if (!batch.length) return false
            page += 1
        }
        return false
    }, [hasMoreHistory, loadMoreMessages])

    const selectRoom = useCallback(async (room) => {
        setActiveRoom(room)
        await loadMessages(room.id)
        try {
            await chatApi.markRead(room.id)
            setRooms(prev => prev.map(r =>
                r.id === room.id ? { ...r, unread_count: 0 } : r
            ))
        } catch { /* silent */ }
    }, [loadMessages])

    const sendMessage = useCallback(async (content, options = {}) => {
        if (!activeRoom) return null
        const trimmed = (content || '').trim()
        if (!trimmed && !options.fileUrl) return null
        try {
            const msg = await chatApi.sendMessage(activeRoom.id, {
                content: trimmed,
                message_type: options.messageType || (options.fileUrl ? 'file' : 'text'),
                reply_to_id: options.replyToId || null,
                file_url: options.fileUrl || null,
                file_name: options.fileName || null,
                file_size: options.fileSize ?? null,
                mentioned_user_ids: options.mentionedUserIds?.length ? options.mentionedUserIds : undefined,
            })
            return msg
        } catch (e) {
            console.error('[Chat] Send message error:', e)
            return null
        }
    }, [activeRoom])

    const deleteMessage = useCallback(async (messageId) => {
        if (!messageId) return false
        try {
            const res = await chatApi.deleteMessage(messageId)
            if (res?.ok) {
                setMessages(prev => prev.map(m =>
                    m.id === messageId
                        ? { ...m, is_deleted: true, content: 'Сообщение удалено' }
                        : m
                ))
            }
            return !!res?.ok
        } catch (e) {
            console.error('[Chat] Delete message error:', e)
            return false
        }
    }, [])

    const createDirectChat = useCallback(async (otherUserId) => {
        try {
            const room = await chatApi.createDirectRoom(otherUserId)
            await loadRooms()
            if (room?.id) {
                setActiveRoom(room)
                await loadMessages(room.id)
            }
            return room
        } catch (e) {
            console.error('[Chat] Create direct chat error:', e)
            throw e
        }
    }, [loadRooms, loadMessages])

    const createGroupRoom = useCallback(async (payload) => {
        try {
            const room = await chatApi.createGroupRoom(payload)
            await loadRooms()
            if (room?.id) {
                setActiveRoom(room)
                await loadMessages(room.id)
            }
            return room
        } catch (e) {
            console.error('[Chat] Create group room error:', e)
            return null
        }
    }, [loadRooms, loadMessages])

    const pinRoomMessage = useCallback(async (messageId) => {
        if (!activeRoom?.id) return null
        try {
            const room = await chatApi.pinRoomMessage(activeRoom.id, messageId)
            if (room?.id) {
                setRooms(prev => prev.map(r => (r.id === room.id ? { ...r, ...room } : r)))
                setActiveRoom(prev => (prev && prev.id === room.id ? { ...prev, ...room } : prev))
            }
            return room
        } catch (e) {
            console.error('[Chat] Pin message error:', e)
            return null
        }
    }, [activeRoom?.id])

    const markMessageRead = useCallback(async (messageId) => {
        try {
            await chatApi.markMessageRead(messageId)
        } catch {
            /* silent */
        }
    }, [])

    const leaveRoom = useCallback(async (roomId) => {
        try {
            await chatApi.leaveRoom(roomId)
            setRooms(prev => prev.filter(r => r.id !== roomId))
            if (activeRoom?.id === roomId) {
                setActiveRoom(null)
                setMessages([])
            }
            return true
        } catch (e) {
            console.error('[Chat] Leave room error:', e)
            return false
        }
    }, [activeRoom?.id])

    useEffect(() => {
        const unread = rooms.reduce((sum, r) => sum + (r.unread_count || 0), 0)
        setTotalUnread(unread)
    }, [rooms])

    return useMemo(() => ({
        rooms,
        activeRoom,
        messages,
        isLoading,
        isConnected,
        typingUsers,
        totalUnread,
        hasMoreHistory,
        isLoadingMore,
        connect,
        disconnect,
        loadRooms,
        loadMoreMessages,
        ensureMessageLoaded,
        selectRoom,
        sendMessage,
        deleteMessage,
        sendTyping,
        createDirectChat,
        createGroupRoom,
        pinRoomMessage,
        setActiveRoom,
        markMessageRead,
        leaveRoom,
    }), [
        rooms, activeRoom, messages, isLoading, isConnected,
        typingUsers, totalUnread, hasMoreHistory, isLoadingMore,
        connect, disconnect, loadRooms, loadMoreMessages, ensureMessageLoaded,
        selectRoom, sendMessage, deleteMessage, sendTyping,
        createDirectChat, createGroupRoom, pinRoomMessage, markMessageRead,
        leaveRoom,
    ])
}
